import React, { useEffect, useState } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import { getPrograms } from "../../requesters/Backend/ProgramRequester"
import DashboardTable from "./Programs/DashboardTable"
import CreateProgramModal from "./Programs/CreateProgramModal"
import Pagination from "../Pagination"

const PER_PAGE = 25

function BackendDashboard({ baseUrl, currentUser }) {
  const location = useLocation()
  const navigate = useNavigate()
  const initialParams = new URLSearchParams(location.search)

  const [programs, setPrograms] = useState([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(parseInt(initialParams.get("page") || "1", 10))
  const [query, setQuery] = useState(initialParams.get("search") || "")
  const [debouncedQuery, setDebouncedQuery] = useState(initialParams.get("search") || "")
  const [status, setStatus] = useState(initialParams.get("status") || "active")
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), 300)
    return () => clearTimeout(t)
  }, [query])

  useEffect(() => {
    const params = { page, per_page: PER_PAGE }
    if (status === "active" || status === "inactive") params.status = status
    if (debouncedQuery) params.search = debouncedQuery

    setIsLoading(true)
    getPrograms(baseUrl, params, currentUser).then(res => {
      setPrograms(res?.response || [])
      setTotal(parseInt(res?.headers?.["x-total"] || "0", 10))
      return null
    }).catch(err => {
      // eslint-disable-next-line no-console
      console.log(err)
    }).finally(() => {
      setIsLoading(false)
    })
  }, [baseUrl, currentUser, page, debouncedQuery, status])

  useEffect(() => {
    const params = new URLSearchParams()
    params.set("page", String(page))
    params.set("status", status)
    if (debouncedQuery) params.set("search", debouncedQuery)
    const next = `?${params.toString()}`
    if (location.search === next) return
    navigate({ pathname: location.pathname, search: next }, { replace: true })
  }, [page, debouncedQuery, status, navigate, location.pathname, location.search])

  const handleStatus = event => {
    setStatus(event.target.value)
    setPage(1)
  }

  const handleSearch = event => {
    setQuery(event.target.value)
    setPage(1)
  }

  const hasPrograms = programs.length > 0

  return (
    <div className="content">
      <div className="container-fluid">
        <div className="page-header">
          <div className="d-flex justify-content-between align-items-center">
            <h2>Programs</h2>
            <span>
              <CreateProgramModal baseUrl={baseUrl} currentUser={currentUser} />
            </span>
          </div>
        </div>
        <div className="table-filters d-inline-flex">
          <input
            type="search"
            className="form-control"
            placeholder="e.g. 30 Days Boost"
            value={query}
            onChange={handleSearch} />
          <select className="form-control" onChange={handleStatus} value={status}>
            <option value="all">Status: All</option>
            <option value="active">Status: Active</option>
            <option value="inactive">Status: Inactive</option>
          </select>
        </div>
        {isLoading && (
          <div className="d-flex justify-content-center align-items-center mt-4">
            <div className="spinner-border text-primary" role="status">
              <span className="sr-only">Loading...</span>
            </div>
          </div>
        )}
        {!isLoading && hasPrograms && (
          <>
            <div className="list-table-wrap">
              <div className="table-responsive">
                <DashboardTable data={programs} />
              </div>
            </div>
            <Pagination
              key={`${debouncedQuery}-${status}`}
              totalRows={total}
              pageChangeHandler={setPage}
              rowsPerPage={PER_PAGE}
              initialPage={page} />
          </>
        )}
        {!isLoading && !hasPrograms && (
          <p className="text-muted mt-3 mb-0 text-center">No programs found</p>
        )}
      </div>
    </div>
  )
}

export default BackendDashboard
